import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Check } from "lucide-react";
import api from "../lib/api";
import { useAuth, formatError } from "../context/AuthContext";
import { Logo } from "../components/common";

export default function Signup() {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [params] = useSearchParams();
  const [packages, setPackages] = useState([]);
  const [selected, setSelected] = useState(params.get("package") || null);
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [agree, setAgree] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/packages").then(({ data }) => {
      setPackages(data);
      if (!selected && data.length) setSelected(data.find((p) => p.featured)?.id || data[0].id);
    });
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!agree) return toast.error("Please accept the terms and health disclaimer.");
    setLoading(true);
    try {
      localStorage.setItem("im_selected_package", selected || "");
      await register({ ...form, role: "customer" });
      toast.success("Account created!");
      navigate("/onboarding");
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Sign up failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] py-10 px-5">
      <div className="max-w-4xl mx-auto">
        <Link to="/"><Logo /></Link>
        <h1 className="font-heading text-5xl mt-8">Join Iron Mountain</h1>
        <p className="text-secondary mt-1">Pick your plan and create your account.</p>

        <div className="grid lg:grid-cols-2 gap-8 mt-8">
          <div>
            <span className="label-xs">1. Choose your plan</span>
            <div className="space-y-3 mt-3">
              {packages.map((p) => (
                <button key={p.id} type="button" data-testid={`signup-pkg-${p.id}`} onClick={() => setSelected(p.id)}
                  className={`w-full text-left rounded-xl p-4 border transition-all ${selected === p.id ? "border-brand bg-brand/10" : "border-border card-surface hover:border-white/30"}`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-semibold flex items-center gap-2">{p.name}
                        {selected === p.id && <Check className="w-4 h-4 text-brand" />}</div>
                      <div className="text-secondary text-xs mt-0.5">{p.included_workouts} workouts · {p.checkins} check-ins</div>
                    </div>
                    <div className="font-heading text-2xl">${p.monthly_price}<span className="text-xs text-secondary">/mo</span></div>
                  </div>
                </button>
              ))}
            </div>
          </div>

          <form onSubmit={submit}>
            <span className="label-xs">2. Create your account</span>
            <div className="space-y-4 mt-3">
              <input data-testid="signup-name" placeholder="Full name" required value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full bg-surface-elevated border border-border rounded-lg px-4 py-3 focus:outline-none focus:border-brand" />
              <input data-testid="signup-email" type="email" placeholder="Email" required value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="w-full bg-surface-elevated border border-border rounded-lg px-4 py-3 focus:outline-none focus:border-brand" />
              <input data-testid="signup-password" type="password" placeholder="Password" required value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                className="w-full bg-surface-elevated border border-border rounded-lg px-4 py-3 focus:outline-none focus:border-brand" />
              <label className="flex items-start gap-3 text-sm text-secondary cursor-pointer">
                <input data-testid="signup-agree" type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)}
                  className="mt-1 accent-[#FF3B30]" />
                <span>I accept the Terms, waiver, and cancellation policy, and understand this app does not replace professional medical advice.</span>
              </label>
              <button data-testid="signup-submit" disabled={loading}
                className="w-full py-3 bg-brand text-white font-bold uppercase tracking-wide rounded-lg hover:bg-brand-hover transition-all disabled:opacity-60">
                {loading ? "Creating..." : "Create Account"}
              </button>
              <p className="text-secondary text-sm text-center">
                Already a member? <Link to="/login" className="text-brand font-semibold">Log in</Link>
              </p>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
